import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { UserButton, useUser } from "@clerk/clerk-react";
import { useQuery } from "@tanstack/react-query";
import {
  LayoutDashboard, MessagesSquare, Users2, Bot, ShieldCheck, ListTodo, Workflow,
  UploadCloud, FolderLock, Images, Download, Plug, Bell, ScrollText, Building2,
  Settings2, Search, ChevronDown, Headset, CreditCard, Network, Inbox, Mail, Newspaper, MessageSquareQuote, Mails, Globe, TrendingUp, Share2,
  HelpCircle, Users, Briefcase,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useApi } from "@/lib/api";
import { useBrand } from "@/lib/brand";
import { BRAND_ACCENTS } from "@/lib/ui";
import CommandPalette from "@/components/CommandPalette";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV = [
  {
    group: "Core · Daily",
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
      { to: "/approvals", label: "Approvals", icon: ShieldCheck },
      { to: "/tasks", label: "Tasks", icon: ListTodo },
      { to: "/leads", label: "Lead Inbox", icon: Inbox },
      { to: "/payments", label: "Payments", icon: CreditCard },
    ],
  },
  {
    group: "Command & AI",
    items: [
      { to: "/chief", label: "Chief of Staff", icon: MessagesSquare },
      { to: "/agents", label: "AI Agents", icon: Bot },
      { to: "/concierge", label: "Concierge", icon: Headset },
      { to: "/meeting", label: "Meeting Room", icon: Users2 },
    ],
  },
  {
    group: "Data & Performance",
    items: [
      { to: "/ecosystem", label: "GGG Live Data", icon: Network },
      { to: "/growth", label: "Growth & Marketing", icon: TrendingUp },
      { to: "/social", label: "Social Studio", icon: Share2 },
    ],
  },
  {
    group: "Brands & Content",
    items: [
      { to: "/brands", label: "Brands", icon: Building2 },
      { to: "/blog", label: "Blog", icon: Newspaper },
      { to: "/gallery", label: "Gallery", icon: Images },
      { to: "/testimonials", label: "Testimonials", icon: MessageSquareQuote },
      { to: "/faq", label: "FAQ", icon: HelpCircle },
      { to: "/team", label: "Team", icon: Users },
      { to: "/services", label: "Services", icon: Briefcase },
      { to: "/newsletter", label: "Newsletter", icon: Mails },
    ],
  },
  {
    group: "Assets & Resources",
    items: [
      { to: "/uploads", label: "Upload Centre", icon: UploadCloud },
      { to: "/documents", label: "Document Vault", icon: FolderLock },
      { to: "/media", label: "Media Library", icon: Images },
      { to: "/downloads", label: "Download Centre", icon: Download },
      { to: "/email-templates", label: "Email Templates", icon: Mail },
    ],
  },
  {
    group: "System & Settings",
    items: [
      { to: "/automations", label: "Automations", icon: Workflow },
      { to: "/integrations", label: "Integrations", icon: Plug },
      { to: "/notifications", label: "Notifications", icon: Bell },
      { to: "/audit", label: "Audit Log", icon: ScrollText },
      { to: "/website-settings", label: "Website Settings", icon: Globe },
      { to: "/settings", label: "Settings", icon: Settings2 },
    ],
  },
];

function BrandSwitcher() {
  const api = useApi();
  const { brand, setBrand } = useBrand();
  const { data: brands = [] } = useQuery({ queryKey: ["brands"], queryFn: () => api.get("/brands") });
  const active = brands.find((b) => b.code === brand) || brands.find((b) => b.code === "GGG");
  const accent = BRAND_ACCENTS[brand] || BRAND_ACCENTS.GGG;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="brand-switcher"
          className="flex items-center gap-2.5 rounded-md border border-border bg-surface px-3 py-1.5 hover:bg-accent transition-colors shrink-0 whitespace-nowrap"
        >
          <span className="h-2.5 w-2.5 rounded-sm shrink-0" style={{ background: `hsl(${accent})` }} />
          <span className="text-sm font-medium whitespace-nowrap">{active?.name || "GGG"}</span>
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-60 bg-popover border-border">
        {brands.map((b) => (
          <DropdownMenuItem
            key={b.code}
            data-testid={`brand-option-${b.code}`}
            onClick={() => setBrand(b.code)}
            className="flex items-center justify-between gap-2 cursor-pointer"
          >
            <span className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-sm" style={{ background: `hsl(${BRAND_ACCENTS[b.code]})` }} />
              {b.name}
            </span>
            <span className={cn("text-[9px] font-mono uppercase tracking-wider",
              b.state === "ACTIVE" ? "text-ok" : "text-muted-foreground")}>
              {b.state === "ACTIVE" ? "Active" : "Soon"}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Layout({ children }) {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const api = useApi();
  const navigate = useNavigate();
  const { user } = useUser();

  const { data: notif } = useQuery({
    queryKey: ["notif-count"],
    queryFn: () => api.get("/notifications?unread=true"),
    refetchInterval: 30000,
  });

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="min-h-screen flex">
      <aside className="w-60 shrink-0 border-r border-border bg-surface/60 hidden md:flex flex-col fixed inset-y-0 z-30">
        <div className="h-14 flex items-center gap-2 px-4 border-b border-border">
          <img src="/ggg-logo.webp" alt="Gopi Gadhvi Group" className="h-9 w-auto" />
          <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-primary">Command</span>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-6">
          {NAV.map((section) => (
            <div key={section.group}>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground/70 px-2 mb-2">
                {section.group}
              </div>
              <div className="space-y-0.5">
                {section.items.map((item) => (
                  item.soon ? (
                    <div
                      key={item.label}
                      data-testid={`nav-soon-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className="flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-muted-foreground/50 cursor-default"
                    >
                      <item.icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
                      <span className="flex-1">{item.label}</span>
                      <span className="text-[9px] font-mono uppercase tracking-wider border border-primary/30 text-primary/70 rounded-full px-1.5 py-0.5">Soon</span>
                    </div>
                  ) : (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors",
                        isActive
                          ? "bg-accent text-foreground border-l-2 border-primary pl-2"
                          : "text-muted-foreground hover:text-foreground hover:bg-accent/50",
                      )
                    }
                  >
                    <item.icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
                    {item.label}
                  </NavLink>
                  )
                ))}
              </div>
            </div>
          ))}
        </nav>
        <div className="p-3 border-t border-border text-[10px] font-mono text-muted-foreground/60">
          PHASE 01 · CORE
        </div>
      </aside>

      <div className="flex-1 md:ml-60 flex flex-col min-w-0">
        <header className="h-14 border-b border-border glass sticky top-0 z-20 flex items-center gap-3 px-4 sm:px-6">
          <button
            data-testid="open-search"
            onClick={() => setPaletteOpen(true)}
            className="flex items-center gap-2 rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-muted-foreground hover:bg-accent transition-colors w-48 sm:w-72"
          >
            <Search className="h-4 w-4" />
            <span className="flex-1 text-left">Search…</span>
            <kbd className="text-[10px] font-mono border border-border rounded px-1">⌘K</kbd>
          </button>
          <div className="flex-1" />
          <BrandSwitcher />
          <button
            data-testid="topbar-notifications"
            onClick={() => navigate("/notifications")}
            className="relative rounded-md border border-border bg-surface h-9 w-9 flex items-center justify-center hover:bg-accent transition-colors shrink-0"
          >
            <Bell className="h-4 w-4" />
            {notif?.unread > 0 && (
              <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-mono flex items-center justify-center">
                {notif.unread}
              </span>
            )}
          </button>
          <UserButton afterSignOutUrl="/sign-in" />
        </header>

        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1500px] w-full fade-up">{children}</main>
      </div>

      <CommandPalette open={paletteOpen} setOpen={setPaletteOpen} />
    </div>
  );
}
